// The gate's consignment log: one day (‹ day › stepper), or a search across all days — "which truck
// brought challan CH-7781?" — by challan number, PO number, vendor or vehicle.

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ClipboardList, DoorOpen, Loader2, RefreshCw, Search, X } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gateApi, isoDay, type Consignment } from "@/lib/gate";
import { useGate } from "@/lib/session-scope";
import { ConsignmentCard } from "@/components/consignment-card";
import { ConsignmentSheet } from "@/components/consignment-sheet";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

/** "3 Oct 2026" for a yyyy-MM-dd day. */
function showDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function shiftDay(day: string, by: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return isoDay(new Date(y, m - 1, d + by));
}

export default function Log() {
  const { token } = useAuth();
  const { gate } = useGate();
  const today = isoDay();
  const [day, setDay] = useState(today);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<Consignment[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const h = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reload on gate / day / search / refresh
    setLoading(true);
    setError(null);
    gateApi
      .consignments(token, gate.id, query ? { search: query } : { day }, controller.signal)
      .then((d) => setRows(d?.rows ?? []))
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setError(e instanceof ApiError ? e.message : "Could not load the log.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, gate.id, day, query, nonce]);

  const scope = query ? `matching “${query}”` : day === today ? "today" : `on ${showDay(day)}`;

  return (
    <div className="flex w-full flex-col gap-4 px-4 pt-4 pb-8 md:px-6">
      <div className="flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <DoorOpen className="size-5" />
        </span>
        <div className="min-w-0">
          <div className="truncate text-base leading-tight font-semibold">{gate.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {[gate.operationUnit?.name, query ? "All days" : day === today ? "Today" : showDay(day)]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
        <div className="relative min-w-0 md:w-[26rem]">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Challan, PO number, vendor or vehicle"
            className="h-11 rounded-xl bg-card pr-9 pl-9 text-base md:text-base"
            enterKeyHint="search"
          />
          {search && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Clear search"
              className="absolute top-1/2 right-1 -translate-y-1/2"
              onClick={() => setSearch("")}
            >
              <X />
            </Button>
          )}
        </div>
        {!query && (
          <div className="flex items-center gap-2 md:max-w-md md:flex-1">
            <Button
              variant="outline"
              aria-label="Previous day"
              className="size-11 rounded-xl bg-card"
              onClick={() => setDay(shiftDay(day, -1))}
            >
              <ChevronLeft className="size-5" />
            </Button>
            <DatePicker
              value={day}
              onChange={(v) => v && setDay(v)}
              className="h-11 min-w-0 flex-1 rounded-xl bg-card"
            />
            <Button
              variant="outline"
              aria-label="Next day"
              className="size-11 rounded-xl bg-card"
              disabled={day >= today}
              onClick={() => setDay(shiftDay(day, 1))}
            >
              <ChevronRight className="size-5" />
            </Button>
            <Button
              variant="outline"
              aria-label="Refresh"
              className="size-11 rounded-xl bg-card"
              onClick={() => setNonce((n) => n + 1)}
            >
              {loading ? <Loader2 className="size-5 animate-spin" /> : <RefreshCw className="size-5" />}
            </Button>
          </div>
        )}
      </div>
      {!query && day !== today && (
        <Button variant="link" className="-mt-2 self-center md:self-start" onClick={() => setDay(today)}>
          Back to today
        </Button>
      )}

      {error ? (
        <p className="rounded-xl bg-destructive/10 p-4 text-sm text-destructive">{error}</p>
      ) : loading && !rows ? (
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-36 w-full rounded-2xl" />
          ))}
        </div>
      ) : !rows?.length ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-card px-6 py-10 text-center">
          <ClipboardList className="size-10 text-muted-foreground/50" />
          <p className="font-semibold">No consignments</p>
          <p className="text-sm text-muted-foreground">
            Nothing recorded at {gate.name} {scope}.
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm font-semibold">
            {rows.length} consignment{rows.length === 1 ? "" : "s"} at {gate.name} {scope}
            {query && rows.length === 100 ? " (latest 100)" : ""}
          </p>
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {rows.map((c) => (
              <ConsignmentCard
                key={c.id}
                c={c}
                onOpen={() => setOpenId(c.id)}
                onChanged={() => setNonce((n) => n + 1)}
              />
            ))}
          </div>
        </>
      )}

      <ConsignmentSheet id={openId} onClose={() => setOpenId(null)} onChanged={() => setNonce((n) => n + 1)} />
    </div>
  );
}
